import { Injectable, Type } from '@angular/core';

/**
 * Pilha de navegação DENTRO do modal — o que o `<ion-nav>` fazia.
 *
 * No master a `ModalPage` é `<ion-nav [root]="root">`, e telas abertas em modal empilham
 * outra tela por cima sem fechar o modal: `document.querySelector('ion-nav').push(LoginPage)`.
 * A desionização trocou o `ion-nav` por render dinâmico do root, e aquela busca passou a
 * devolver null em todo lugar. Onde havia guarda (`if (nav)`) o código caiu no fallback e
 * mudou de comportamento; onde NÃO havia — `use-loyalty-dialog` e `save-card-dialog` — virou
 * `TypeError: Cannot read properties of null (reading 'push')`, com o botão morto na tela.
 *
 * Este serviço é o registro que o `document.querySelector` fazia: a `ModalPage` se inscreve
 * enquanto está viva e as telas chamam `push`/`pop` sem conhecer quem está por baixo. O
 * retorno booleano preserva a forma do código original (`if (nav) return nav.push(X)`), para
 * o fallback de rota continuar valendo quando não há modal aberto.
 *
 * Tipado por interface, não pela ModalPage: importar a página aqui fecharia ciclo de import
 * (ver memória do barrel de core).
 */
export interface NavegavelEmModal {
	/** Empilha uma tela por cima da atual, mantendo o modal aberto. */
	pushEmModal(component: Type<any>): void;
	/** Volta para a tela anterior da pilha. Falso se já estava na raiz. */
	popEmModal(): boolean;
}

@Injectable({ providedIn: 'root' })
export class ModalNavService {
	private readonly abertos: NavegavelEmModal[] = [];

	registrar(modal: NavegavelEmModal): void {
		this.abertos.push(modal);
	}

	desregistrar(modal: NavegavelEmModal): void {
		const i = this.abertos.lastIndexOf(modal);
		if (i >= 0) this.abertos.splice(i, 1);
	}

	/** O modal mais recente — o equivalente ao `document.querySelector('ion-nav')`. */
	get topo(): NavegavelEmModal | null {
		return this.abertos.length ? this.abertos[this.abertos.length - 1] : null;
	}

	/** Empilha no modal aberto. Devolve falso se não há modal — aí vale o fallback de rota. */
	push(component: Type<any>): boolean {
		const topo = this.topo;
		if (!topo) return false;
		topo.pushEmModal(component);
		return true;
	}

	/** Desempilha no modal aberto. Falso se não há modal, ou se ele já está na raiz. */
	pop(): boolean {
		const topo = this.topo;
		return topo ? topo.popEmModal() : false;
	}
}
